import { useEffect } from 'react';
import isEqual from 'lodash/isEqual';

import type { FetchMaterialsFilters } from '../../materialsTypes';
import type {
  CacheFetchMaterialsAggregationsQueryResponse,
  CacheFetchMaterialsAggregationsQueryResponseAggregations
} from '../../queries/fetchCacheMaterialsAggregations.query';

import { CACHE_FETCH_MATERIALS_AGGREGATIONS_QUERY } from '../../queries/fetchCacheMaterialsAggregations.query';
import { FETCH_SELECT_MATERIALS_QUERY } from '../../queries/fetchSelectMaterials.query';

import { useCurrentUser } from '../../../../auth/hooks/useAuth';
import { useCacheMaterials } from '../useCacheMaterials';

import { MaterialsPermissions } from '../../materialsConstants';

import { MaterialCache } from '../../MaterialCache';

type UseCacheMaterialsAggregationsOptions = {
  materialsFilters: FetchMaterialsFilters;
};

function useCacheMaterialsAggregations({
  materialsFilters
}: UseCacheMaterialsAggregationsOptions) {
  const currentUser = useCurrentUser();

  const withSearchMaterials = currentUser.hasPermissions(
    MaterialsPermissions.READ_MATERIALS_AGGREGATIONS
  );

  const {
    materialsAggregations,
    materialsFetched,
    materialsLoading,
    filterMaterials: changeMaterialsAggregationsFilter,
    materialsFilters: materialsAggregationsFilters
  } = useCacheMaterials<
    CacheFetchMaterialsAggregationsQueryResponse,
    unknown,
    CacheFetchMaterialsAggregationsQueryResponseAggregations
  >({
    additionalParams: { aggregations: true },
    cacheKey: MaterialCache.aggregationsMaterialsCacheKey(),
    cacheQuery: CACHE_FETCH_MATERIALS_AGGREGATIONS_QUERY,
    initialFilters: materialsFilters,
    query: FETCH_SELECT_MATERIALS_QUERY,
    trackTotalHits: true,
    options: {
      enabled: withSearchMaterials,
      enabledPlaceholder: false
    }
  });

  useEffect(() => {
    if (
      withSearchMaterials &&
      !isEqual(materialsFilters, materialsAggregationsFilters)
    ) {
      changeMaterialsAggregationsFilter({
        ...materialsFilters
      });
    }
  }, [
    materialsFilters,
    materialsAggregationsFilters,
    changeMaterialsAggregationsFilter,
    withSearchMaterials
  ]);

  return {
    materialsAggregations: withSearchMaterials ? materialsAggregations : {},
    materialsAggregationsFetched:
      !withSearchMaterials || (materialsFetched && !materialsLoading)
  };
}

export default useCacheMaterialsAggregations;
